import {
  LicenseVerificationRoute,
  OpenAiOnboardingDocumentUpload,
  OnboardingRepository,
  OnboardingSnapshot,
  PeerAdviceRoute,
  RecapResult,
  SoftwareExperiencePercentage,
  SubmitAiOnboardingProfile,
  TrustedOnboardingProfileFields,
  ValidateAiOnboardingProfile,
  VerifyPharmacistLicense,
  VerifyPharmacistLicenseResult,
} from './types';
import {
  formatValidationErrors,
  saveOnboardingDetailsSchema,
  showOnboardingRecapSchema,
  updateProfileNamesSchema,
  validateClientToolParameters,
  verifyPharmacistLicenseSchema,
} from './validation';
import {
  normalizePharmacistLicenseNumber,
} from './license';
import {
  getAiOnboardingContactPhoneNumber,
  getAiOnboardingFinalProfileFields,
  getProfileWithCalculatedYearsOfExperience,
  mapOnboardingDetails,
  normalizeAiOnboardingPhoneNumber,
  normalizeAiOnboardingPostalCode,
} from './onboardingMappers';
import { normalizeOnboardingCatalogFields } from './catalog';
import { createDocumentUploadClientTools } from './documentUploadTools';
import { createProfileSectionClientTools } from './profileSectionTools';
import {
  isRecord,
  normalizeNestedRemainingSoftwarePercentage,
} from './toolParameters';
import { TypeOfUser } from '../helpers/Constants';

export interface ClientToolDependencies {
  getRepository: () => OnboardingRepository;
  openDocumentUpload: OpenAiOnboardingDocumentUpload;
  openRecap: () => Promise<RecapResult>;
  signupRequestId: string;
  submitFinalProfile: SubmitAiOnboardingProfile;
  switchToManualMode: () => void;
  getTrustedProfileFields: () => TrustedOnboardingProfileFields;
  updateProfileNames: (profileNames: {
    firstName: string;
    lastName: string;
  }) => void | Promise<void>;
  validateProfile?: ValidateAiOnboardingProfile;
  verifyPharmacistLicense: VerifyPharmacistLicense;
}

enum ClientToolErrorMessage {
  LicenseVerificationFailed = 'Licence verification failed',
}

// The pharmacist is asked to repeat the licence number once before the
// attempt is left for the PharmaPlan reviewer.
const maxLicenseVerificationAttempts = 2;

const minPeerAdviceYearsOfExperience = 2;

// Only a result where both the licence number and the name failed asks the
// professional to repeat the number, and only while attempts remain. Any other
// result continues silently so a reviewer can follow up.
const getLicenseVerificationRoute = (
  verification: VerifyPharmacistLicenseResult,
  attemptCount: number,
) => {
  const isBothUnverified = !verification.licenseVerified
    && !verification.nameVerified;
  const hasAttemptsRemaining = attemptCount < maxLicenseVerificationAttempts;

  if (isBothUnverified && hasAttemptsRemaining) {
    return LicenseVerificationRoute.Retry;
  }

  return LicenseVerificationRoute.Continue;
};

// Only professionals with enough relief work are asked for advice to give
// newcomers. Deciding it here keeps the skip out of the LLM's hands.
const getPeerAdviceRoute = (yearsOfExperience: number | null | undefined) => (
  typeof yearsOfExperience === 'number'
  && yearsOfExperience >= minPeerAdviceYearsOfExperience
    ? PeerAdviceRoute.Ask
    : PeerAdviceRoute.Skip
);

const validateProfileWithoutManualSchema: ValidateAiOnboardingProfile = async () =>
  [];

const includeTrustedProfileNames = (
  parameters: unknown,
  trustedProfileFields: TrustedOnboardingProfileFields,
) => {
  if (!isRecord(parameters)) {
    return parameters;
  }

  const { fields } = parameters;

  if (!isRecord(fields)) {
    return parameters;
  }

  return {
    ...parameters,
    fields: {
      ...fields,
      firstName: trustedProfileFields.firstName,
      lastName: trustedProfileFields.lastName,
    },
  };
};

// Any completed verification attempt counts, whatever its outcome, because an
// unverified licence is still saved for a reviewer to follow up on. A retried
// save that drops the licence number must not fail on a number the professional
// already gave.
const includeVerifiedLicenseNumber = (
  parameters: unknown,
  snapshot: OnboardingSnapshot,
) => {
  const isVerificationComplete = snapshot.licenseVerified !== null
    && snapshot.nameVerified !== null;
  const licenseNumber = isVerificationComplete
    ? snapshot.onboardingDetails?.licenseNumber
    : null;

  if (!licenseNumber || !isRecord(parameters)) {
    return parameters;
  }

  const { fields } = parameters;

  if (!isRecord(fields)) {
    return parameters;
  }

  const suppliedLicenseNumber = fields.licenseNumber;

  if (
    typeof suppliedLicenseNumber === 'string'
    && Boolean(suppliedLicenseNumber.trim())
  ) {
    return parameters;
  }

  return {
    ...parameters,
    fields: {
      ...fields,
      licenseNumber,
    },
  };
};

const createRecapFailure = (
  errorCode:
  | 'VALIDATION_ERROR'
  | 'RECAP_ERROR'
  | 'PROFILE_SUBMISSION_ERROR',
) =>
  ({
    success: false,
    recapConfirmed: false,
    fieldsCorrected: false,
    correctedFieldNames: [] as string[],
    profileSubmitted: false,
    errorCode,
  });

export const createOnboardingClientTools = ({
  getRepository,
  openDocumentUpload,
  openRecap,
  signupRequestId,
  submitFinalProfile,
  switchToManualMode,
  getTrustedProfileFields,
  updateProfileNames,
  validateProfile = validateProfileWithoutManualSchema,
  verifyPharmacistLicense,
}: ClientToolDependencies) => {
  let finalProfileSubmission: Promise<void> | null = null;
  let licenseVerificationAttemptCount = 0;

  const submitFinalProfileOnce: SubmitAiOnboardingProfile = (
    profile,
    editedProps,
  ) => {
    if (!finalProfileSubmission) {
      finalProfileSubmission = submitFinalProfile(
        profile,
        editedProps,
      ).catch((error) => {
        finalProfileSubmission = null;
        throw error;
      });
    }

    return finalProfileSubmission;
  };

  const saveCompleteProfile = async (parameters: unknown) => {
    const repository = getRepository();
    const trustedProfileFields = getTrustedProfileFields();
    let pharmacistSnapshot: OnboardingSnapshot | null = null;
    let parametersToValidate = normalizeNestedRemainingSoftwarePercentage(
      includeTrustedProfileNames(parameters, trustedProfileFields),
    );

    if (trustedProfileFields.role === TypeOfUser.pharmacist) {
      try {
        pharmacistSnapshot = await repository.getSnapshot();
        parametersToValidate = includeVerifiedLicenseNumber(
          parametersToValidate,
          pharmacistSnapshot,
        );
      } catch {
        return {
          success: false,
          errorCode: 'LOCAL_STATE_ERROR',
        };
      }
    }

    const validation = await validateClientToolParameters(
      saveOnboardingDetailsSchema,
      parametersToValidate,
    );

    if (!validation.success) {
      return {
        success: false,
        errorCode: 'VALIDATION_ERROR',
        validationErrors: formatValidationErrors(validation.error),
      };
    }

    const { catalogs, ...trustedFields } = trustedProfileFields;
    const profileFields = {
      ...validation.data.fields,
      ...trustedFields,
    };
    const catalogNormalization = catalogs
      ? normalizeOnboardingCatalogFields(
        profileFields,
        catalogs,
        validation.data.remainingSoftwarePercentage
          ? Number(validation.data.remainingSoftwarePercentage) as
            SoftwareExperiencePercentage
          : null,
      )
      : { fields: profileFields, validationErrors: [] };

    if (catalogNormalization.validationErrors.length > 0) {
      return {
        success: false,
        errorCode: 'VALIDATION_ERROR',
        validationErrors: catalogNormalization.validationErrors,
      };
    }

    const normalizedProfileFields = {
      ...catalogNormalization.fields,
      mobile: normalizeAiOnboardingPhoneNumber(
        catalogNormalization.fields.mobile,
      ),
      phone: normalizeAiOnboardingPhoneNumber(
        getAiOnboardingContactPhoneNumber(catalogNormalization.fields),
      ),
      postalCode: normalizeAiOnboardingPostalCode(
        catalogNormalization.fields.postalCode,
      ),
    };
    const onboardingData = getProfileWithCalculatedYearsOfExperience(
      normalizedProfileFields,
    );
    let validationErrors: string[];

    try {
      validationErrors = await validateProfile(onboardingData);
    } catch {
      return {
        success: false,
        errorCode: 'VALIDATION_ERROR',
        validationErrors: ['input: Unable to validate the supplied details'],
      };
    }

    if (validationErrors.length > 0) {
      return {
        success: false,
        errorCode: 'VALIDATION_ERROR',
        validationErrors,
      };
    }

    try {
      if (onboardingData.role === TypeOfUser.pharmacist) {
        const matchesAttempt = pharmacistSnapshot?.onboardingDetails
        && pharmacistSnapshot.onboardingDetails.licenseNumber
          === onboardingData.licenseNumber
        && pharmacistSnapshot.licenseVerified !== null
        && pharmacistSnapshot.nameVerified !== null;

        if (!matchesAttempt) {
          return {
            success: false,
            errorCode: 'LICENSE_VERIFICATION_REQUIRED',
            validationErrors: [
              'fields.licenseNumber: Run license verification first',
            ],
          };
        }
      }

      await repository.saveOnboardingDetails(
        mapOnboardingDetails(onboardingData),
      );

      return {
        success: true,
        peerAdviceRoute: getPeerAdviceRoute(onboardingData.yearsOfExperience),
      };
    } catch {
      return {
        success: false,
        errorCode: 'LOCAL_STATE_ERROR',
      };
    }
  };

  return ({
    advance_onboarding_stage: async (_parameters: unknown) => ({
      success: true,
    }),

    switch_to_manual_mode: async (_parameters: unknown) => {
      switchToManualMode();

      return {
        success: true,
      };
    },

    verify_pharmacist_license: async (parameters: unknown) => {
      const validation = await validateClientToolParameters(
        verifyPharmacistLicenseSchema,
        parameters,
      );

      if (!validation.success) {
        throw new Error(ClientToolErrorMessage.LicenseVerificationFailed);
      }

      const {
        licenseNumber: unnormalizedLicenseNumber,
      } = validation.data.licenseData;
      const licenseNumber = normalizePharmacistLicenseNumber(
        unnormalizedLicenseNumber,
      );
      const trustedProfileFields = getTrustedProfileFields();
      const firstName = validation.data.licenseData.firstName
        ?? trustedProfileFields.firstName;
      const lastName = validation.data.licenseData.lastName
        ?? trustedProfileFields.lastName;
      let verification;

      try {
        verification = await verifyPharmacistLicense({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          licenseNumber,
        });
      } catch {
        throw new Error(ClientToolErrorMessage.LicenseVerificationFailed);
      }

      licenseVerificationAttemptCount += 1;

      try {
        await getRepository().saveLicenseVerification({
          licenseNumber,
          licenseVerified: verification.licenseVerified,
          nameVerified: verification.nameVerified,
        });

        return {
          success: verification.success,
          licenseVerified: verification.licenseVerified,
          nameVerified: verification.nameVerified,
          confirmedLicenseNumber: licenseNumber,
          confirmedFirstName: firstName.trim(),
          confirmedLastName: lastName.trim(),
          route: getLicenseVerificationRoute(
            verification,
            licenseVerificationAttemptCount,
          ),
        };
      } catch {
        throw new Error(ClientToolErrorMessage.LicenseVerificationFailed);
      }
    },

    update_profile_names: async (parameters: unknown) => {
      const validation = await validateClientToolParameters(
        updateProfileNamesSchema,
        parameters,
      );

      if (!validation.success) {
        return {
          success: false,
          errorCode: 'VALIDATION_ERROR',
          validationErrors: formatValidationErrors(validation.error),
        };
      }

      const firstName = validation.data.profileNames.firstName.trim();
      const lastName = validation.data.profileNames.lastName.trim();

      try {
        await updateProfileNames({ firstName, lastName });
        return {
          success: true,
          firstName,
          lastName,
        };
      } catch {
        return {
          success: false,
          errorCode: 'LOCAL_STATE_ERROR',
        };
      }
    },

    save_onboarding_details: saveCompleteProfile,

    ...createProfileSectionClientTools({
      getRepository,
      getTrustedProfileFields,
      saveCompleteProfile,
      validateProfile,
    }),

    ...createDocumentUploadClientTools({
      openDocumentUpload,
    }),

    show_onboarding_recap: async (parameters: unknown) => {
      const validation = await validateClientToolParameters(
        showOnboardingRecapSchema,
        parameters,
      );

      if (!validation.success) {
        return createRecapFailure('VALIDATION_ERROR');
      }

      let result: RecapResult;

      try {
        result = await openRecap();
      } catch {
        return createRecapFailure('RECAP_ERROR');
      }

      try {
        if (result.confirmed) {
          const completionSnapshot = await getRepository().getSnapshot();
          const profile = getAiOnboardingFinalProfileFields(
            completionSnapshot,
            signupRequestId,
          );
          await submitFinalProfileOnce(
            profile,
            result.correctedFieldNames,
          );
        }
      } catch {
        return createRecapFailure('PROFILE_SUBMISSION_ERROR');
      }

      return {
        success: true,
        recapConfirmed: result.confirmed,
        fieldsCorrected: result.fieldsCorrected,
        correctedFieldNames: result.correctedFieldNames,
        profileSubmitted: result.confirmed,
      };
    },
  });
};

export type OnboardingClientTools = ReturnType<
  typeof createOnboardingClientTools
>;
